import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Link
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined,
  ArrowBack
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getLoginDepartment } from '../constants/loginDepartments';
import FloatingFeedbackButton from './feedback/FloatingFeedbackButton';
import LoginBackgroundVideo, { FALLBACK_BACKGROUND } from './LoginBackgroundVideo';

const NAVY = '#12294a';

// The background video carries the Fleetwright wordmark, so the heading is hidden
// from sight while the video plays but kept in the DOM for screen readers.
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f3f4f6',
    borderRadius: { xs: '12px', sm: '15px' },
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: '#d7dae0' },
    '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '1.5px' }
  },
  '& .MuiOutlinedInput-input': {
    paddingTop: { xs: '16px', sm: '22px', md: '27px' },
    paddingBottom: { xs: '16px', sm: '22px', md: '27px' },
    fontSize: { xs: '1rem', sm: '1.05rem', md: '1.125rem' }
  }
};

const Login = () => {
  const { department: departmentSlug } = useParams();
  const department = getLoginDepartment(departmentSlug);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoAvailable, setVideoAvailable] = useState(true);
  const { login } = useAuth();

  if (!department) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password, department.slug);

    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: { xs: 'center', sm: 'flex-end' },
        justifyContent: { xs: 'center', sm: 'flex-end' },
        pl: { xs: 'max(12px, env(safe-area-inset-left))', sm: 3 },
        pr: { xs: 'max(12px, env(safe-area-inset-right))', sm: 3, lg: 4 },
        pt: { xs: 'max(16px, env(safe-area-inset-top))', sm: 4 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
        boxSizing: 'border-box',
        background: FALLBACK_BACKGROUND,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <LoginBackgroundVideo onAvailabilityChange={setVideoAvailable} />

      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 420, sm: 520, md: 657 },
          my: { xs: 1, sm: 0 },
          p: { xs: 2.5, sm: 3.5, md: 5.15 },
          borderRadius: { xs: '18px', sm: '24px' },
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          border: '1px solid rgba(255, 255, 255, 0.28)',
          backdropFilter: 'blur(22px) saturate(160%)',
          WebkitBackdropFilter: 'blur(22px) saturate(160%)',
          boxShadow: '0 24px 60px rgba(8, 20, 40, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.35)'
        }}
      >
        <Typography
          component="h1"
          variant="h4"
          sx={
            videoAvailable
              ? VISUALLY_HIDDEN
              : {
                  mb: 1.5,
                  fontWeight: 700,
                  color: '#fff',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }
          }
        >
          FLEETWRIGHT
        </Typography>

        <Link
          component={RouterLink}
          to="/login"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: { xs: 1.75, sm: 2.6 },
            color: 'rgba(255,255,255,0.82)',
            fontSize: { xs: '0.875rem', sm: '0.95rem' },
            fontWeight: 500,
            textShadow: '0 1px 8px rgba(0,0,0,0.3)'
          }}
        >
          <ArrowBack sx={{ fontSize: '1.1rem' }} />
          All departments
        </Link>

        <Typography
          sx={{
            fontSize: { xs: '1.35rem', sm: '1.65rem', md: '1.98rem' },
            fontWeight: 700,
            color: '#fff',
            lineHeight: 1.2,
            textShadow: '0 1px 12px rgba(0,0,0,0.35)'
          }}
        >
          {department.name} Login
        </Typography>
        <Typography
          sx={{
            mt: 0.65,
            mb: { xs: 2.5, sm: 3.5, md: 4.4 },
            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.21rem' },
            color: 'rgba(255,255,255,0.82)',
            textShadow: '0 1px 8px rgba(0,0,0,0.3)'
          }}
        >
          Sign in to continue to {department.name}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, sm: 2.6 }, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            required
            autoFocus
            id="email"
            name="email"
            type="email"
            placeholder="Email address"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ ...fieldSx, mb: { xs: 2, sm: 3, md: 3.9 } }}
            InputProps={{
              'aria-label': 'Email address',
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ color: '#9096a1', fontSize: { xs: '1.35rem', sm: '1.7rem' } }} />
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            required
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ ...fieldSx, mb: { xs: 3, sm: 4, md: 5.85 } }}
            InputProps={{
              'aria-label': 'Password',
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: '#9096a1', fontSize: { xs: '1.35rem', sm: '1.7rem' } }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ color: '#9096a1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    ) : (
                      <Visibility sx={{ color: '#9096a1', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            sx={{
              py: { xs: 1.5, sm: 2, md: 2.73 },
              borderRadius: { xs: '12px', sm: '15px' },
              textTransform: 'none',
              fontSize: { xs: '1.15rem', sm: '1.45rem', md: '1.85rem' },
              fontWeight: 600,
              backgroundColor: NAVY,
              '&:hover': { backgroundColor: '#0d1f3a' }
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </Box>
      </Paper>

      <FloatingFeedbackButton />
    </Box>
  );
};

export default Login;
